import React, { useState, useCallback, memo } from 'react'
import {
    Modal, ModalOverlay, ModalContent, ModalHeader, ModalFooter,
    ModalBody, Stack, Textarea, Text, VStack, Box, HStack,
    useToast
} from '@chakra-ui/react'
import { IoIosRadioButtonOn, IoIosRadioButtonOff } from "react-icons/io"
import pkg from "@inrscr/coschool-ui-components";
import { isEmptyOrNull } from '../../../utils/common-utils';

// Memoize the RadioOption component to prevent unnecessary re-renders
const RadioOption = memo(({ value, selectedOption, onOptionChange, text, component, LatexRenderView }) => (
    <Box onClick={() => onOptionChange(value)} display="flex" alignItems="flex-start" cursor="pointer">
        {selectedOption === value ?
            <IoIosRadioButtonOn
                size={24} color="#1C9E4E"
                style={{ minWidth: '24px', marginRight: '16px', marginTop: text ? '2px' : 0 }}
            />
            :
            <IoIosRadioButtonOff
                size={24} color="#130159"
                style={{ minWidth: '24px', marginRight: '16px', marginTop: text ? '2px' : 0 }}
            />
        }
        {/* <Text w={"90%"} as="div" color="#130159">{text}</Text> */}
        {text && <LatexRenderView math={text} />}
        {component && component()}
    </Box>
));

export default function PocketModal({ isOpen, handleSubmit, handleClose, contextQuery }) {
    const { CustomButton, LatexRenderView } = pkg;
    const [state, setState] = useState({
        option: isEmptyOrNull(contextQuery) ? 'custom' : 'default',
        description: ''
    });
    const toast = useToast();

    // Memoize handlers to prevent re-renders
    const handleOptionChange = useCallback((value) => {
        setState(prev => ({ ...prev, option: value }));
    }, []);

    const handleDescriptionChange = useCallback((e) => {
        setState(prev => ({ ...prev, description: e.target.value }));
    }, []);

    const onSubmit = useCallback(() => {
        handleSubmit(state?.description);
    }, [handleSubmit, state]);

    // Memoize the textarea component
    const TextAreaComponent = useCallback(() => (
        <Textarea
            value={state.description}
            onChange={handleDescriptionChange}
            placeholder="Send your description"
            isDisabled={state.option !== 'custom'}
            fontSize="16px"
            w="90%"
            sx={{
                h: '80px',
                border: "1px solid #21212140",
                borderRadius: "8px",
                boxShadow: "sm",
                resize: 'none',
                _disabled: { opacity: 0.4, bg: 'transparent' },
            }}
            _placeholder={{ color: "#13015980", opacity: 1 }}
        />
    ), [state.description, state.option, handleDescriptionChange]);

    return (
        <Modal isOpen={isOpen} onClose={handleClose} size="2xl" closeOnEsc={false} closeOnOverlayClick={false}>
            <ModalOverlay />
            <ModalContent bgColor="#F0EEFF" p="16px 24px 16px 32px">
                <ModalHeader p={0}>
                    <Text as="div" sx={{ color: "#130159", fontWeight: 600, fontSize: "24px" }}>Send to pocket</Text>
                </ModalHeader>
                <ModalBody p="8px 0px 48px 8px">
                    <VStack spacing={4} alignItems="flex-start">
                        <Text w="90%" fontSize="16px" fontWeight="400" color="#130159D9">
                            You are about to send the following question to the pocket. Alternatively, you can send your description instead.
                        </Text>
                        <Stack spacing={8} w="full" mt={6}>
                            {!isEmptyOrNull(contextQuery) &&
                                <RadioOption
                                    value="default"
                                    selectedOption={state.option}
                                    onOptionChange={handleOptionChange}
                                    text={contextQuery}
                                    LatexRenderView={LatexRenderView}
                                />
                            }
                            <RadioOption
                                value="custom"
                                selectedOption={state.option}
                                onOptionChange={handleOptionChange}
                                component={TextAreaComponent}
                            />
                        </Stack>
                    </VStack>
                </ModalBody>

                <ModalFooter w={"full"} pb={0} pl={-8}>
                    <HStack alignItems={"center"} justifyContent={"space-between"} w={"full"}>
                        <Text
                            onClick={handleClose}
                            as={"div"}
                            sx={{ cursor: "pointer", fontWeight: "600", fontSize: "16px", bg: "#F0EEFF", color: "#130159D9" }}
                        >
                            Cancel
                        </Text>
                        <CustomButton
                            handleClick={onSubmit}
                            label="Send to pocket"
                            customStyles={{ h: 10, fontSize: "16px", color: "#FFFFFF", bg: "#5F4DC7" }}
                            isDisabled={state.option === 'custom' && !state.description.trim()}
                        />
                    </HStack>
                </ModalFooter>
            </ModalContent>
        </Modal>
    )
}